/**
 * 🌾 **HAY — CUT, AND IT IS HAY AT ONCE.** D131, 2026-09-30.
 *
 * 🎯 **haugur:** *"Cut, and it is hay at once"* · *"Home field and meadows"* ·
 * *"Whenever grass has grown"* · *"A stone ljár for now"* · *"yes"* to the table.
 *
 * | | |
 * |---|---|
 * | 🔪 **The ljár, right button** on `tun` | **1 `tada`** — the best sheep feed |
 * | … on `myri` or `gras` | **1 `uthey`** — good feed |
 * | 🟫 **What is left** | **stubble**, `slaegja[of=<the field>]` |
 * | 🌱 **10 game days later** | **the field it was**, any time of year |
 *
 * ## 🔴 Two halves, and ADR-0009 §2 decides which goes where
 *
 * **WHAT the field was** is three values and never more, so it is a **block
 * state** — the number in the chunk says *cut tún* or *cut mýri*, and nothing
 * else has to be read to know what grows back. **WHEN it was cut** is
 * open-ended, so it is a **`block_data` row**, in played milliseconds — the
 * clock that only runs while somebody is in the world, which is the clock
 * every other *"N game days"* in this game is counted on.
 *
 * ⚠️ **Why a timer and not Minecraft's random ticks** (`Minecraft/03` §5,
 * `Minecraft/08` §1.4): a random tick is right for something that is
 * *everywhere and stateless* — and stubble is neither. It exists only where
 * somebody cut, and haugur's number is **10 days**, not *"about 10, sometimes
 * 30"*. Luanti's rule of thumb says it outright: **scan the common and
 * stateless; timer the rare and stateful.**
 *
 * ⚠️ **NOT in it, on his word:** drying, weather, rope, a hay barn, the iron
 * ljár. And ⚠️ **grass growth by heat (D43) was never built** — 10 days is the
 * berries' pattern, not a model of Icelandic grass.
 */

import { blockIdForKey, blockById } from './blocks.ts';
import {
  REAL_MS_PER_GAME_DAY,
  WORLD_START_DAY_FRACTION,
  WORLD_START_DAY_OF_YEAR,
} from './clock.ts';
import { growingDaysBetween } from './fodder.ts';
import { baseBlockOf, stateOf, stateIdForKey } from './states.ts';

/** 🔪 The scythe. */
export const LJAR_ITEM = 'ljar';
/** 🟫 The block a cut field becomes. */
export const STUBBLE_BLOCK = 'slaegja';
/** 🌱 How long stubble stands before the field is back — haugur's number. */
export const STUBBLE_REGROW_GAME_DAYS = 10;
/**
 * 🗄️ **The one field the stubble's `block_data` row holds.**
 *
 * 🔴 **A constant, because SQL reads it as a STRING** — `blockdata.ts` filters on
 * it, and `listBurningPositions`' own note records a field that was renamed in
 * TypeScript while a query went on asking for the old name, **so every fire in
 * the world stopped going out.** One name, used by both.
 */
export const STUBBLE_CUT_FIELD = 'cutAtPlayedMs';

/**
 * 🌾 **What each field gives when it is cut** — and, by being in this list,
 * which blocks CAN be cut. ⚠️ **The order of `of` in `block-states.json` is
 * `tun, myri, gras`** and a test pins that every key here has a state there.
 */
const HAY_FROM: ReadonlyMap<string, string> = new Map([
  ['tun', 'tada'],
  ['myri', 'uthey'],
  ['gras', 'uthey'],
]);

/** Every field key the ljár cuts — for the client's right-click, which asks by key. */
export const CUTTABLE_FIELDS: ReadonlySet<string> = new Set(HAY_FROM.keys());

/**
 * 🌾 **The hay this block gives to a ljár, or `undefined` if it is not a field.**
 *
 * ⚠️ **Stubble answers `undefined`** — it is its own block, not `tun` — so a cut
 * field cannot be cut again until it has grown back. That IS *"whenever grass
 * has grown."*
 */
export function hayFrom(blockId: number): string | undefined {
  const key = blockById(blockId)?.key;
  return key === undefined ? undefined : HAY_FROM.get(key);
}

/**
 * 🟫 **The stubble this field leaves behind**, as a number for the chunk —
 * `undefined` if the block is not a field.
 *
 * 🔴 **Asked by state KEY, never built from a base id**, so a state that was
 * never locked fails loudly here instead of quietly growing back as cut tún.
 */
export function stubbleFor(fieldId: number): number | undefined {
  const key = blockById(fieldId)?.key;
  if (key === undefined || !HAY_FROM.has(key)) return undefined;
  /* `tun` is the default, so it is the BASE id and has no state key of its own. */
  return key === 'tun'
    ? blockIdForKey(STUBBLE_BLOCK)
    : stateIdForKey(`${STUBBLE_BLOCK}[of=${key}]`);
}

/** Is this number stubble, in any of its three states? */
export function isStubble(id: number): boolean {
  return blockById(baseBlockOf(id))?.key === STUBBLE_BLOCK;
}

/**
 * 🌱 **The field under this stubble** — what grows back, and what digging it
 * gives you. `undefined` if the number is not stubble.
 */
export function fieldUnderStubble(id: number): number | undefined {
  if (!isStubble(id)) return undefined;
  const of = stateOf(id)['of'];
  return blockIdForKey(typeof of === 'string' ? of : 'tun');
}

/** How many played milliseconds stubble stands for. */
export const STUBBLE_REGROW_MS = STUBBLE_REGROW_GAME_DAYS * REAL_MS_PER_GAME_DAY;

/**
 * 🌱 **Has this stubble grown back?**
 *
 * ⚠️ **A missing or broken time answers YES.** A stubble whose row is lost
 * would otherwise stand for ever, and a field that grows back early is a far
 * smaller wrong than one that never does.
 */
export function stubbleHasRegrown(cutAtPlayedMs: unknown, nowPlayedMs: number): boolean {
  if (typeof cutAtPlayedMs !== 'number' || !Number.isFinite(cutAtPlayedMs)) return true;
  /* ❄️ T286 — 🎯 haugur: grass does not grow back November to April. Only
     May–October counts toward the ten days, so stubble cut or grazed in the
     autumn stands until the spring and the flock lives on stored hay. */
  return (
    growingDaysBetween(
      cutAtPlayedMs,
      nowPlayedMs,
      WORLD_START_DAY_OF_YEAR,
      WORLD_START_DAY_FRACTION,
      REAL_MS_PER_GAME_DAY,
    ) >=
    STUBBLE_REGROW_GAME_DAYS - 1e-9
  );
}
